import { snowflakeApi } from '../utils/api';
import blobService from './storage/blobService';

export interface ClientFile {
  id?: number;
  client_id: string;
  client_name?: string;
  filename: string;
  file_path: string; // Full blob URL including domain
  file_size?: number;
  file_type?: string;
  file_category?: string; // New field for file category
  load_status?: string; // New field for load status
  created_at?: string;
  date_from?: string; // Start date of data range
  date_to?: string; // End date of data range
  workflow_id?: string; // Workflow ID for linking files to specific workflows
}

export interface UploadProgress {
  filename: string;
  bytesTransferred: number;
  totalBytes: number;
  percentComplete: number;
}

// File categories as shown in the screenshot
export const FILE_CATEGORIES = [
  'Google Search Console', 
  'Google Ads',
  'Google Business Profile'
] as const;

export type FileCategory = typeof FILE_CATEGORIES[number];

class ClientFilesService {
  private static instance: ClientFilesService;

  public static getInstance(): ClientFilesService {
    if (!ClientFilesService.instance) {
      ClientFilesService.instance = new ClientFilesService();
    }
    return ClientFilesService.instance;
  }

  /**
   * Upload a file for a client - saves to blob storage and records in Snowflake
   * Uses direct upload to Azure Blob Storage for optimal performance
   * Backend controls access via SAS token generation
   */
  async uploadClientFile(
    file: File,
    clientId: string,
    clientName: string,
    fileCategory: FileCategory,
    onProgress?: (progress: UploadProgress) => void,
    dateFrom?: string,
    dateTo?: string,
    workflowId?: string
  ): Promise<ClientFile> {
    try {
      // Step 1: Check if there's already a file for this category for the same client/workflow
      await this.handleExistingFileForCategory(clientId, fileCategory, workflowId);

      // Step 2: Upload to blob storage with correct path structure
      // Generate unique filename to prevent overwrites
      const timestamp = Date.now();
      const extension = file.name.includes('.') 
        ? file.name.substring(file.name.lastIndexOf('.'))
        : '';
      const baseName = file.name.includes('.')
        ? file.name.substring(0, file.name.lastIndexOf('.'))
        : file.name;
      
      const uniqueFileName = `${baseName}-${timestamp}${extension}`;
      
      console.log(`Uploading file: ${uniqueFileName} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      
      // Direct upload to Azure Blob Storage using SAS token
      // Backend controls access via SAS token generation
      const relativeBlobPath = await blobService.uploadFileDirectly(file, {
        containerName: 'client-data',
        blobName: uniqueFileName, // Pass only the filename, backend constructs full path
        clientId,
        workflowId: 'general', // Use 'general' for client-level files
        dataSource: 'client-files', // Required for the UploadOptions interface
        onProgress
      });

      // Step 3: Get the full blob URL with domain
      // Extract container name and blob name from the relative path
      const pathParts = relativeBlobPath.split('/');
      const containerName = pathParts[0]; // "client-data"
      const blobName = pathParts.slice(1).join('/'); // "157/input-files/filename.csv"
      
      const fullBlobUrl = await blobService.getDownloadUrl(blobName, containerName);

      // Step 4: Record in Snowflake database with full blob URL
      // This notifies the backend that the upload is complete
      const savedFile = await this.recordFileInDatabase({
        client_id: clientId,
        client_name: clientName,
        filename: file.name,
        file_path: fullBlobUrl, // Full blob URL including domain
        file_size: file.size,
        file_type: file.type,
        file_category: fileCategory,
        load_status: 'Pending', // Set initial status to 'Pending'
        date_from: dateFrom,
        date_to: dateTo,
        workflow_id: workflowId // Add workflow ID to the record
      });

      return savedFile;
    } catch (error) {
      console.error('Error uploading client file:', error);
      throw error;
    }
  }

  /**
   * Handle existing files for the same category - mark them as 'To Delete'
   * Only marks files for the same workflow if workflowId is provided
   */
  private async handleExistingFileForCategory(clientId: string, fileCategory: FileCategory, workflowId?: string): Promise<void> {
    try {
      // Build URL with optional workflow_id query parameter
      let url = `/api/v1/client-files/client/${clientId}/category/${fileCategory}/mark-for-deletion`;
      if (workflowId) {
        url += `?workflow_id=${encodeURIComponent(workflowId)}`;
      }
      
      const response = await snowflakeApi.put(url);
      const workflowMsg = workflowId ? ` for workflow ${workflowId}` : '';
      console.log(`Marked existing files for category ${fileCategory}${workflowMsg} as 'To Delete'`);
    } catch (error) {
      console.error('Error handling existing files for category:', error);
      // Don't throw here - continue with upload even if this fails
    }
  }

  /**
   * Record file information in Snowflake database
   */
  private async recordFileInDatabase(fileData: ClientFile): Promise<ClientFile> {
    try {
      const response = await snowflakeApi.post('/api/v1/client-files/', fileData);
      return response.data;
    } catch (error) {
      console.error('Error recording file in database:', error);
      throw error;
    }
  }

  /**
   * Get all files for a client
   */
  async getClientFiles(clientId: string): Promise<ClientFile[]> {
    try {
      const response = await snowflakeApi.get(`/api/v1/client-files/client/${clientId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting client files:', error);
      return [];
    }
  }

  /**
   * Get files for a specific client and workflow
   */
  async getClientWorkflowFiles(clientId: string, workflowId: string): Promise<ClientFile[]> {
    try {
      const response = await snowflakeApi.get(`/api/v1/client-files/client/${clientId}/workflow/${workflowId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting client workflow files:', error);
      return [];
    }
  }

  /**
   * Delete a client file - soft delete by updating status to 'To Delete'
   */
  async deleteClientFile(fileId: number): Promise<void> {
    try {
      // Soft delete by updating the load_status to 'To Delete'
      await snowflakeApi.put(`/api/v1/client-files/${fileId}/soft-delete`);
      
    } catch (error) {
      console.error('Error deleting client file:', error);
      throw error;
    }
  }

  /**
   * Get download URL for a client file
   */
  async getFileDownloadUrl(filePath: string): Promise<string> {
    try {
      let blobName: string;
      const containerName = 'client-data';

      if (filePath.startsWith('https://')) {
        // For full URLs, extract everything after 'client-data/'
        const clientDataIndex = filePath.indexOf('client-data/');
        if (clientDataIndex >= 0) {
          // Get the path after 'client-data/' but before any query parameters
          const remainingPath = filePath.substring(clientDataIndex + 'client-data/'.length);
          const questionMarkIndex = remainingPath.indexOf('?');
          blobName = questionMarkIndex >= 0 ? remainingPath.substring(0, questionMarkIndex) : remainingPath;
        } else {
          throw new Error('Invalid file path: missing client-data container');
        }
      } else {
        // Handle relative path - remove 'client-data/' prefix if present
        blobName = filePath.startsWith('client-data/') 
          ? filePath.substring('client-data/'.length)
          : filePath;
      }

      // Always generate a fresh download URL with new SAS token
      return await blobService.getDownloadUrl(blobName, containerName);
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }
}

export default ClientFilesService.getInstance(); 